import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback, useRef } from 'react'
import { api, apiV1 } from '../services/api'

interface User {
  _id: string
  email: string
  firstName: string
  lastName: string
  role: 'SUPPORT_ADMIN' | 'SUPER_ADMIN'
  permissions: string[]
  isActive: boolean
  lastLogin?: string
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  isAuthenticated: boolean
  /**
   * Returns true when the current user is allowed to use a given permission
   * key. SUPER_ADMIN is always allowed.
   */
  hasPermission: (permission: string) => boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const hasInitialized = useRef(false)

  useEffect(() => {
    // Prevent multiple initializations
    if (hasInitialized.current) return
    hasInitialized.current = true

    const initializeAuth = async () => {
      const token = localStorage.getItem('adminToken')
      if (token) {
        apiV1.defaults.headers.common['Authorization'] = `Bearer ${token}`
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`
        try {
          const response = await apiV1.get('/auth/me')
          // Backend returns either { data: { ...user } } or { data: { user: {...} } }.
          // Support both shapes so refreshing the page restores the session.
          const payload = response.data?.data ?? {}
          const userData = payload.user ?? payload
          if (userData && (userData.id || userData._id)) {
            setUser({
              _id: userData.id ?? userData._id,
              email: userData.email,
              firstName: userData.firstName,
              lastName: userData.lastName,
              role: userData.role,
              permissions: Array.isArray(userData.permissions) ? userData.permissions : [],
              isActive: userData.isActive,
              lastLogin: userData.lastLogin
            })
          }
        } catch (error) {
          localStorage.removeItem('adminToken')
          delete api.defaults.headers.common['Authorization']
          delete apiV1.defaults.headers.common['Authorization']
        }
      }
      setLoading(false)
    }

    initializeAuth()
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await apiV1.post('/auth/login', { email, password })
      const { tokens, user: userData } = response.data.data
      const { accessToken, refreshToken } = tokens
      
      // Store tokens
      localStorage.setItem('adminToken', accessToken)
      localStorage.setItem('refreshToken', refreshToken)
      api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
      apiV1.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
      
      // Set user data
      setUser({
        _id: userData.id,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        role: userData.role,
        permissions: Array.isArray(userData.permissions) ? userData.permissions : [],
        isActive: userData.isActive,
        lastLogin: userData.lastLogin
      })
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed')
    }
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem('adminToken')
    localStorage.removeItem('refreshToken')
    delete api.defaults.headers.common['Authorization']
    setUser(null)
  }, [])

  const hasPermission = useCallback((permission: string): boolean => {
    if (!user) return false
    if (user.role === 'SUPER_ADMIN') return true
    return Array.isArray(user.permissions) && user.permissions.includes(permission)
  }, [user])

  const value = useMemo(() => ({
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    hasPermission,
  }), [user, loading, login, logout, hasPermission])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

